

import React, { useState, useEffect } from 'react';
import { Order, Language, OrderStatus, UserRole } from '../types';
import { TRANSLATIONS } from '../translations';
import { ChevronLeft, AlertTriangle, CheckCircle, TrendingUp, Eye, Heart, Clock, Send, ShieldAlert, CheckSquare } from 'lucide-react';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';

interface OrderMonitoringProps {
  order: Order;
  language: Language;
  onBack: () => void;
  userRole?: UserRole; // Optional: If we want to hide actions for certain roles
  onSubmitWork?: (orderId: string) => void;
  onApproveOrder?: (orderId: string) => void;
  onRaiseDispute?: (orderId: string) => void;
}

export const OrderMonitoring: React.FC<OrderMonitoringProps> = ({ 
    order, 
    language, 
    onBack, 
    userRole = UserRole.PROJECT, // Defaulting to Project for now if not passed
    onSubmitWork,
    onApproveOrder,
    onRaiseDispute
}) => {
  const t = TRANSLATIONS[language];
  const data = order.monitoring;
  
  // Timer State
  const [timeLeft, setTimeLeft] = useState<string>('');
  const [progressPercent, setProgressPercent] = useState(0);

  useEffect(() => {
    if (order.status === OrderStatus.SUBMITTED && order.submissionTime) {
        const interval = setInterval(() => {
            const now = new Date().getTime();
            const submitTime = new Date(order.submissionTime!).getTime();
            const deadline = submitTime + (48 * 60 * 60 * 1000); // 48 hours later (Updated from 24)
            const distance = deadline - now;

            if (distance < 0) {
                setTimeLeft("00:00:00");
                setProgressPercent(100);
                clearInterval(interval);
            } else {
                const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
                const seconds = Math.floor((distance % (1000 * 60)) / 1000);

                setTimeLeft(`${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`);
                
                // Calc progress (inverse, because time is running out)
                const totalDuration = 48 * 60 * 60 * 1000; // 48 hours
                const elapsed = totalDuration - distance;
                setProgressPercent((elapsed / totalDuration) * 100);
            }
        }, 1000);

        return () => clearInterval(interval);
    }
  }, [order.status, order.submissionTime]);

  const steps = [
      { id: OrderStatus.NEGOTIATING, label: t.stepNegotiating },
      { id: OrderStatus.ESCROW_PENDING, label: t.stepEscrow },
      { id: OrderStatus.IN_PROGRESS, label: t.stepInProgress },
      { id: OrderStatus.SUBMITTED, label: t.stepSubmitted },
      { id: OrderStatus.COMPLETED, label: t.stepCompleted }
  ];

  const getCurrentStepIndex = () => {
      // Mapping disputed to Submitted for visual continuity or separate
      if (order.status === OrderStatus.DISPUTED) return 3; 
      return steps.findIndex(s => s.id === order.status);
  };

  const currentStepIndex = getCurrentStepIndex();

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300 pb-10">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button 
            onClick={onBack}
            className="p-2 hover:bg-slate-800 rounded-lg text-slate-400 hover:text-white transition-colors"
        >
            <ChevronLeft size={20} />
        </button>
        <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
                {t.monitoringDashboard}
                <span className={`px-2 py-0.5 rounded text-xs font-bold border ${
                    order.status === OrderStatus.COMPLETED ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' : 
                    order.status === OrderStatus.DISPUTED ? 'bg-rose-500/20 text-rose-400 border-rose-500/30' :
                    'bg-indigo-500/20 text-indigo-400 border-indigo-500/30'
                }`}>
                    {order.status}
                </span>
            </h1>
            <p className="text-slate-400 text-sm">{order.projectName} - {order.serviceTitle}</p>
        </div>
      </div>

      {/* Progress Stepper */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 overflow-x-auto">
          <h3 className="text-sm font-bold text-white mb-4 uppercase tracking-wider">{t.contractProgress}</h3>
          <div className="flex items-center min-w-[600px]">
              {steps.map((step, index) => {
                  const isCompleted = index < currentStepIndex || order.status === OrderStatus.COMPLETED;
                  const isCurrent = index === currentStepIndex && order.status !== OrderStatus.COMPLETED;
                  
                  return (
                      <div key={step.id} className="flex-1 flex items-center relative last:flex-none">
                          <div className={`relative z-10 flex items-center justify-center w-8 h-8 rounded-full border-2 transition-colors ${
                              isCompleted ? 'bg-emerald-500 border-emerald-500 text-white' :
                              isCurrent ? 'bg-indigo-600 border-indigo-500 text-white animate-pulse' :
                              'bg-slate-900 border-slate-600 text-slate-500'
                          }`}>
                              {isCompleted ? <CheckCircle size={16} /> : <span className="text-xs">{index + 1}</span>}
                              
                              {/* Label */}
                              <span className={`absolute -bottom-6 text-xs font-medium whitespace-nowrap ${
                                  isCurrent ? 'text-indigo-400' : isCompleted ? 'text-emerald-400' : 'text-slate-500'
                              }`}>
                                  {step.label}
                              </span>
                          </div>
                          
                          {/* Connector Line */}
                          {index < steps.length - 1 && (
                              <div className={`flex-1 h-1 mx-2 rounded ${
                                  isCompleted ? 'bg-emerald-500' : 'bg-slate-700'
                              }`}></div>
                          )}
                      </div>
                  );
              })}
          </div>
      </div>

      {/* Action Panel (Dynamic based on Status) */}
      {order.status === OrderStatus.IN_PROGRESS && userRole === UserRole.KOL && (
          <div className="bg-gradient-to-r from-indigo-900/50 to-slate-900/50 border border-indigo-500/30 rounded-xl p-6 flex flex-col md:flex-row items-center justify-between gap-4">
              <div>
                  <h3 className="text-lg font-bold text-white mb-1">{t.stepInProgress}</h3>
                  <p className="text-sm text-slate-400">If you have completed the deliverables, submit your work to start the 48h review timer.</p>
              </div>
              <button 
                onClick={() => onSubmitWork && onSubmitWork(order.id)}
                className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-lg shadow-lg shadow-indigo-500/20 flex items-center gap-2"
              >
                  <Send size={18} /> {t.markAsDone}
              </button>
          </div>
      )}

      {order.status === OrderStatus.SUBMITTED && (
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
              <div className="flex flex-col md:flex-row gap-6">
                  {/* Timer Side */}
                  <div className="flex-1 bg-slate-900/50 rounded-lg p-5 border border-slate-700 flex flex-col items-center justify-center text-center">
                      <div className="mb-2 flex items-center gap-2 text-indigo-400 font-bold uppercase tracking-widest text-xs">
                          <Clock size={14} /> {t.autoReleaseTimer}
                      </div>
                      <div className="text-4xl font-mono font-bold text-white mb-2">
                          {timeLeft}
                      </div>
                      <div className="w-full bg-slate-700 h-2 rounded-full mb-2 overflow-hidden">
                          <div 
                             className="h-full bg-indigo-500 transition-all duration-1000 linear"
                             style={{ width: `${progressPercent}%` }}
                          ></div>
                      </div>
                      <p className="text-xs text-slate-500">{t.autoReleaseDesc}</p>
                  </div>

                  {/* Actions Side */}
                  <div className="flex-1 flex flex-col justify-center">
                      {userRole === UserRole.PROJECT ? (
                          <div className="space-y-4">
                              <h3 className="font-bold text-white">{t.submissionReceived}</h3>
                              <p className="text-sm text-slate-400 mb-4">Please review the deliverables. Funds will be released automatically if no action is taken.</p>
                              <div className="flex gap-3">
                                  <button 
                                    onClick={() => onApproveOrder && onApproveOrder(order.id)}
                                    className="flex-1 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-lg flex items-center justify-center gap-2"
                                  >
                                      <CheckSquare size={18} /> {t.confirmRelease}
                                  </button>
                                  <button 
                                    onClick={() => onRaiseDispute && onRaiseDispute(order.id)}
                                    className="flex-1 py-3 bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 border border-rose-500/30 font-bold rounded-lg flex items-center justify-center gap-2"
                                  >
                                      <ShieldAlert size={18} /> {t.enterArbitration}
                                  </button>
                              </div>
                          </div>
                      ) : (
                          <div className="flex flex-col items-center justify-center h-full text-center">
                              <div className="w-12 h-12 bg-indigo-500/20 rounded-full flex items-center justify-center mb-3">
                                  <CheckCircle size={24} className="text-indigo-400" />
                              </div>
                              <h3 className="font-bold text-white mb-1">{t.waitingForApproval}</h3>
                              <p className="text-sm text-slate-400">The project has 48 hours to review your work.</p>
                          </div>
                      )}
                  </div>
              </div>
          </div>
      )}

      {data ? (
        <>
            {/* AI Insight Box */}
            <div className={`p-6 rounded-xl border ${data.status === 'on_track' ? 'bg-emerald-500/10 border-emerald-500/30' : data.status === 'at_risk' ? 'bg-yellow-500/10 border-yellow-500/30' : 'bg-rose-500/10 border-rose-500/30'} flex items-start gap-4`}>
                <div className={`p-2 rounded-lg bg-slate-900/50 ${data.status === 'on_track' ? 'text-emerald-400' : data.status === 'at_risk' ? 'text-yellow-400' : 'text-rose-400'}`}>
                    {data.status === 'on_track' ? <CheckCircle size={24} /> : <AlertTriangle size={24} />}
                </div>
                <div>
                    <h3 className={`font-bold ${data.status === 'on_track' ? 'text-emerald-400' : data.status === 'at_risk' ? 'text-yellow-400' : 'text-rose-400'} mb-1 flex items-center gap-2`}>
                        {t.aiSupervisor}: 
                        <span>{data.status === 'on_track' ? t.statusNormal : data.status === 'at_risk' ? t.statusRisk : t.statusCritical}</span>
                    </h3>
                    <p className="text-slate-200 text-sm leading-relaxed">{data.aiInsight}</p>
                </div>
            </div>

            {/* Main metrics grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Views Progress */}
                <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 relative overflow-hidden">
                    <div className="flex justify-between items-start mb-4">
                        <div>
                            <p className="text-slate-400 text-sm">{t.views}</p>
                            <h3 className="text-3xl font-bold text-white mt-1">{data.currentViews.toLocaleString()}</h3>
                        </div>
                        <Eye className="text-indigo-400" />
                    </div>
                    <div className="w-full bg-slate-700 h-2 rounded-full mb-2">
                        <div 
                            className="bg-indigo-500 h-2 rounded-full transition-all duration-1000" 
                            style={{ width: `${Math.min(100, (data.currentViews / data.targetViews) * 100)}%` }}
                        ></div>
                    </div>
                    <div className="flex justify-between text-xs">
                        <span className="text-slate-400">{t.current}</span>
                        <span className="text-slate-400">{t.target}: {data.targetViews.toLocaleString()}</span>
                    </div>
                </div>

                {/* Likes */}
                <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
                    <div className="flex justify-between items-start mb-4">
                        <div>
                            <p className="text-slate-400 text-sm">{t.likes}</p>
                            <h3 className="text-3xl font-bold text-white mt-1">{data.currentLikes.toLocaleString()}</h3>
                        </div>
                        <Heart className="text-pink-400" />
                    </div>
                    <div className="flex items-center gap-2 mt-4">
                        <TrendingUp size={16} className="text-emerald-400" />
                        <span className="text-sm text-emerald-400 font-medium">4.2% {t.engagement}</span>
                    </div>
                </div>

                {/* Time Elapsed */}
                <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
                    <div className="flex justify-between items-start mb-4">
                        <div>
                            <p className="text-slate-400 text-sm">{t.timeElapsed}</p>
                            <h3 className="text-3xl font-bold text-white mt-1">{data.timeElapsedPercent}%</h3>
                        </div>
                        <Clock className={(data.status === 'at_risk' || data.status === 'critical') ? 'text-rose-400' : 'text-slate-400'} />
                    </div>
                    <div className="w-full bg-slate-700 h-2 rounded-full mb-2">
                        <div 
                            className={`h-2 rounded-full transition-all duration-1000 ${(data.status === 'at_risk' || data.status === 'critical') ? 'bg-rose-500' : 'bg-slate-500'}`}
                            style={{ width: `${data.timeElapsedPercent}%` }}
                        ></div>
                    </div>
                </div>
            </div>

            {/* Chart */}
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
                <h3 className="text-lg font-bold text-white mb-6">{t.liveTracking}</h3>
                <div className="h-72 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={data.hourlyData}>
                        <defs>
                        <linearGradient id="colorViews" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="colorTarget" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#94a3b8" stopOpacity={0.1}/>
                            <stop offset="95%" stopColor="#94a3b8" stopOpacity={0}/>
                        </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                        <XAxis dataKey="hour" stroke="#94a3b8" />
                        <YAxis stroke="#94a3b8" />
                        <Tooltip contentStyle={{ backgroundColor: '#1e293b', borderColor: '#334155', color: '#f8fafc' }} />
                        <Area type="monotone" dataKey="target" stroke="#94a3b8" strokeDasharray="5 5" fillOpacity={1} fill="url(#colorTarget)" />
                        <Area type="monotone" dataKey="views" stroke="#6366f1" strokeWidth={3} fillOpacity={1} fill="url(#colorViews)" />
                    </AreaChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </>
      ) : (
          <div className="p-12 text-center text-slate-500 border border-dashed border-slate-700 rounded-xl">
              No monitoring data available for this stage.
          </div>
      )}
    </div>
  );
};
